import { cn } from "@/lib/utils";

const statusConfig = {
  draft: {
    label: "Draft",
    className: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
  },
  // Feb-2026 Stage-6 lifecycle (Sam): once a PDF has been generated
  // for the client, the invoice moves from draft -> active. If the
  // due date passes without full payment it moves to overdue. Both
  // MUST be first-class pills — leaving them off this map made
  // ``statusConfig[status] || statusConfig.draft`` silently mis-
  // label an Active/Overdue invoice as "Draft" (the Prod SCD-105
  // "View PDF didn't update the pill" defect Feb-2026).
  active: {
    label: "Active",
    className: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
  },
  overdue: {
    label: "Overdue",
    className: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300",
  },
  sent: {
    label: "Sent",
    className: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
  },
  partial: {
    label: "Partial",
    className: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
  },
  paid: {
    label: "Paid",
    className: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
  },
  deleted: {
    label: "Deleted",
    className: "bg-stone-100 text-stone-500 line-through dark:bg-stone-800 dark:text-stone-400",
  },
  // Derived status — server computes ``is_outstanding`` from the
  // invoice's due date and payment terms. Never persisted on the
  // document; only ever rendered.
  outstanding: {
    label: "Outstanding",
    className: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300",
  },
};

/**
 * Given an invoice payload, decide which pill to render. The derived
 * "outstanding" pill visually replaces the sent / partial pill so the
 * card can only carry one status at a time (per Sam's directive —
 * cleanest UX, no double-tagging).
 */
export function deriveDisplayStatus(invoice) {
  if (!invoice) return "draft";
  if (invoice.is_outstanding) return "outstanding";
  return invoice.status || "draft";
}

export const StatusBadge = ({ status, className }) => {
  const config = statusConfig[status] || statusConfig.draft;

  return (
    <span
      className={cn(
        "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium",
        config.className,
        className
      )}
      data-testid={`status-badge-${status}`}
    >
      {config.label}
    </span>
  );
};

export default StatusBadge;
